// src/review/canvas/sections/ThreatAssessmentSection.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Card, Stack, Text, Grid, Badge, Group } from '@mantine/core';

/**
 * ThreatAssessmentSection - Shows threat level assessment
 * Pulls data from threat.assessmentData['Threat Assessment']
 * Matches SecurityTabPanel data structure
 */
function ThreatAssessmentSection({ threat }) {
  // Get assessment data from Redux structure
  const assessmentData = threat.assessmentData || {};
  const threatAssessment = assessmentData['Threat Assessment'] || {};

  // Extract threat level from impact data if not in assessment
  const impact = threat.impact || {};
  const impactLevels = Object.values(impact).filter(Boolean);
  const derivedThreatLevel = impactLevels.includes('High') ? 'High' :
                             impactLevels.includes('Moderate') ? 'Moderate' :
                             impactLevels.includes('Low') ? 'Low' : null;

  const threatLevel = threatAssessment.threatLevel || derivedThreatLevel;
  const likelihood = threatAssessment.likelihood;
  const severity = threatAssessment.severity;
  const vulnerabilityLevel = threatAssessment.vulnerabilityLevel;
  const assetValue = threatAssessment.assetValue;

  // Helper to get threat level color
  const getThreatLevelColor = (level) => {
    const levelLower = (level || '').toLowerCase();
    if (levelLower === 'critical') return 'red';
    if (levelLower === 'high') return 'orange';
    if (levelLower === 'moderate') return 'yellow';
    if (levelLower === 'low') return 'blue';
    return 'gray';
  };

  // Check if we have any assessment data
  const hasAssessmentData = threatLevel || likelihood || severity || vulnerabilityLevel || assetValue;

  return (
    <Card withBorder p="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="md" fw={700}>Threat Assessment</Text>
          {threatLevel && (
            <Badge
              size="lg"
              variant="filled"
              color={getThreatLevelColor(threatLevel)}
            >
              {threatLevel}
            </Badge>
          )}
        </Group>

        {hasAssessmentData ? (
          <Grid gutter="md">
            {likelihood && (
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card withBorder p="sm" bg="gray.0">
                  <Stack gap="xs">
                    <Text size="sm" fw={600}>Likelihood</Text>
                    <Badge
                      size="md"
                      variant="light"
                      color={getThreatLevelColor(likelihood)}
                    >
                      {likelihood}
                    </Badge>
                  </Stack>
                </Card>
              </Grid.Col>
            )}

            {severity && (
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card withBorder p="sm" bg="gray.0">
                  <Stack gap="xs">
                    <Text size="sm" fw={600}>Severity</Text>
                    <Badge
                      size="md"
                      variant="light"
                      color={getThreatLevelColor(severity)}
                    >
                      {severity}
                    </Badge>
                  </Stack>
                </Card>
              </Grid.Col>
            )}

            {vulnerabilityLevel && (
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card withBorder p="sm" bg="gray.0">
                  <Stack gap="xs">
                    <Text size="sm" fw={600}>Vulnerability Level</Text>
                    <Badge
                      size="md"
                      variant="light"
                      color={getThreatLevelColor(vulnerabilityLevel)}
                    >
                      {vulnerabilityLevel}
                    </Badge>
                  </Stack>
                </Card>
              </Grid.Col>
            )}

            {assetValue && (
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card withBorder p="sm" bg="gray.0">
                  <Stack gap="xs">
                    <Text size="sm" fw={600}>Asset Value</Text>
                    <Badge
                      size="md"
                      variant="light"
                      color={getThreatLevelColor(assetValue)}
                    >
                      {assetValue}
                    </Badge>
                  </Stack>
                </Card>
              </Grid.Col>
            )}
          </Grid>
        ) : (
          <Text size="sm" c="dimmed">
            No threat assessment available. Complete the security assessment step.
          </Text>
        )}
      </Stack>
    </Card>
  );
}

ThreatAssessmentSection.propTypes = {
  threat: PropTypes.shape({
    threatId: PropTypes.string.isRequired,
    assessmentData: PropTypes.object,
    impact: PropTypes.object,
  }).isRequired,
};

export default ThreatAssessmentSection;
